import { useId, useLayoutEffect, useRef, useState } from 'react';
import { compactVnd } from './format';

export type Bar = { key: string; label: string; value: bigint; hint?: string };

// Signed bars around a zero line: positive = house won (accent), negative = house lost (error red).
export function BarChart({ bars, height = 200, title }: { bars: Bar[]; height?: number; title: string }) {
  const [hover, setHover] = useState<number | null>(null);
  const grad = useId().replace(/:/g, '') + '-bar';
  const box = useRef<HTMLElement>(null);
  const [W, setW] = useState(640);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => { if (e && e.contentRect.width > 0) setW(Math.round(e.contentRect.width)); });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const padL = W < 420 ? 40 : 56, padB = 22, padT = 10;
  const vals = bars.map((b) => Number(b.value));
  const max = Math.max(0, ...vals), min = Math.min(0, ...vals);
  const span = max - min || 1;
  const plotH = height - padB - padT;
  const y = (v: number) => padT + ((max - v) / span) * plotH;
  const step = (W - padL) / Math.max(1, bars.length);
  const bw = Math.max(4, Math.min(28, step * 0.62));
  const ticks = [max, 0, min].filter((v, i, a) => a.indexOf(v) === i);
  const h = hover !== null ? bars[hover] : null;

  return (
    <figure className="chart" ref={box}>
      <div className="chart__readout" aria-live="polite">
        {h ? <><span className="chart__readout-label">{h.hint ?? h.label}</span><span className="num">{compactVnd(h.value)}</span></> : <span className="muted">Chọn một cột để xem số</span>}
      </div>
      <svg viewBox={`0 0 ${W} ${height}`} width={W} height={height} role="img" aria-label={title} onPointerLeave={() => setHover(null)}>
        <defs>
          <linearGradient id={grad} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" className="chart__stop-top" />
            <stop offset="1" className="chart__stop-bottom" />
          </linearGradient>
        </defs>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={padL} x2={W} y1={y(t)} y2={y(t)} className={t === 0 ? 'chart__zero' : 'chart__grid'} />
            {t === 0 && ticks.some((o) => o !== 0 && Math.abs(y(o) - y(0)) < 14) ? null
              : <text x={padL - 8} y={y(t) + 4} className="chart__tick" textAnchor="end">{compactVnd(BigInt(Math.round(t)))}</text>}
          </g>
        ))}
        {bars.map((b, i) => {
          const v = Number(b.value);
          const top = Math.min(y(v), y(0));
          const hgt = Math.max(v === 0 ? 0 : 1, Math.abs(y(v) - y(0)));
          const cx = padL + step * i + step / 2;
          return (
            <g key={b.key} onPointerEnter={() => setHover(i)}>
              <rect x={padL + step * i} y={padT} width={step} height={plotH} className="chart__hit" />
              <rect x={cx - bw / 2} y={top} width={bw} height={hgt} rx={Math.min(4, bw / 4)} fill={v < 0 ? undefined : `url(#${grad})`}
                className={(v < 0 ? 'chart__bar chart__bar--neg' : 'chart__bar') + (hover === i ? ' is-hover' : '')}>
                <title>{`${b.hint ?? b.label}: ${compactVnd(b.value)}`}</title>
              </rect>
              {step >= 44 || i % Math.ceil(44 / step) === 0 ? <text x={cx} y={height - 6} className="chart__tick" textAnchor="middle">{b.label}</text> : null}
            </g>
          );
        })}
      </svg>
    </figure>
  );
}
